import AbstractFormulaTokenVisitor from "./AbstractFormulaTokenVisitor.js";

/**
 * Formula token parser that uses the Shunting-yard algorithm to build a
 * mathematical expression in the form of a tree `FormulaNode` objects.
 *
 * It parses an array of formula tokens by visiting them.
 *
 * See https://en.wikipedia.org/wiki/Shunting-yard_algorithm
 */
export default class FormulaTokenParser extends AbstractFormulaTokenVisitor {
	parse(tokens) {
		this._outputStack = [];
		this._operatorStack = [];
		this._tokens = tokens;

		this.visitAll(this._tokens);

		while (this._operatorStack.length > 0) {
			this._outputStack.push(this._popOperator());
		}

		this._validateOutputStack();

		return this._outputStack[0];
	}

	visitAbstractValueFormulaToken(token) {
		this._outputStack.push(token.asFormulaNode());
	}

	visitAbstractBinaryOperatorFormulaToken(token) {
		this._validateBinaryOperator(token);
		this._maybePopOperatorWith(token);
		this._operatorStack.push(token);
	}

	visitOpeningParenthesisFormulaToken(token) {
		this._validateOpeningParenthesis(token);
		this._operatorStack.push(token);
	}

	visitClosingParenthesisFormulaToken(token) {
		this._validateClosingParenthesis(token);
		this._maybePopOperatorWith(token);
		this._operatorStack.pop();
	}

	_maybePopOperatorWith(token) {
		if (!this._shouldPopOperatorWith(token)) {
			return;
		}
		this._outputStack.push(this._popOperator());
		this._maybePopOperatorWith(token);
	}

	_shouldPopOperatorWith(token) {
		if (this._operatorStack.length === 0) {
			return false;
		}

		if (token.isClosingParenthesis()) {
			return !this._peekOperator().isOpeningParenthesis();
		}

		if (!this._peekOperator().isBinaryOperator()) {
			return false;
		}

		return token.precedence() <= this._peekOperator().precedence();
	}

	_popOperator() {
		const rightOperand = this._outputStack.pop();
		const leftOperand = this._outputStack.pop();
		const token = this._operatorStack.pop();

		this._validateOperatorToken(token);

		return token.asFormulaNode({
			rightOperand,
			leftOperand,
		});
	}

	_validateOutputStack() {
		if (this._outputStack.length !== 1) {
			throw new Error("Parse failed");
		}

		if (!this._outputStack[0].isValid()) {
			throw new Error("Invalid expression");
		}
	}

	_validateOperatorToken(token) {
		if (!token.isParenthesis()) {
			return;
		}
		throw new Error("Invalid parenthesis position");
	}

	_validateOpeningParenthesis(token) {
		const previousToken = this._previousToken(token);
		const nextToken = this._peekToken(token);

		if (previousToken && !previousToken.isOperator()) {
			throw new Error(
				"Opening parenthesis cannot be preceded by something else than an operator",
			);
		}

		if (nextToken && nextToken.isBinaryOperator()) {
			throw new Error(
				"Opening parenthesis cannot be followed by an operator",
			);
		}
	}

	_validateClosingParenthesis(token) {
		const previousToken = this._previousToken(token);
		const nextToken = this._peekToken(token);

		const openingParenthesis = this._operatorStack.find((t) =>
			t.isOpeningParenthesis(),
		);

		if (previousToken && previousToken.isBinaryOperator()) {
			throw new Error(
				"Closing parenthesis cannot be preceded by an operator",
			);
		}

		if (nextToken && !nextToken.isOperator()) {
			throw new Error(
				"Closing parenthesis cannot be followed by something else than an operator",
			);
		}

		if (!openingParenthesis) {
			throw new Error("Parenthesis mismatch");
		}
	}

	_validateBinaryOperator(token) {
		const nextToken = this._peekToken(token);

		if (!nextToken) {
			throw new Error("Binary operator has no right operand");
		}

		if (nextToken.isBinaryOperator()) {
			throw new Error("Invalid right operand for binary operator");
		}

		if (this._outputStack.length === 0) {
			throw new Error("Binary operator has no left operand");
		}
	}

	_peekOperator() {
		return this._operatorStack.at(-1);
	}

	_peekToken(current) {
		return this._tokens[this._tokens.indexOf(current) + 1];
	}

	_previousToken(current) {
		return this._tokens[this._tokens.indexOf(current) - 1];
	}
}
